import Modal from '../Modal';
import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import { Text } from '../../typography';
import styles from './ConditionalActionModal.module.scss';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { getRoute, paths } from '../../../constants/strings';
import { loader } from 'graphql.macro';
import { useInspection } from '../../../hooks/offline-hooks/createInspectionhook';
import { useMemo } from 'react';
import { actionsKeys } from '../../question_menu/conditionalActions/conditionalAction.utils';
import SimpleFileCard from '../../files/SimpleFileCard';
import { Icon } from '@mui/material';
import Button from '../../Button';
import { getLocalTime } from '../../../utilities/time';
import { keys } from '../../../utilities/translator/translation_keys';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from '../../../providers/WorkspaceProvider';

const createTaskMutation = loader('./ConditionalActionModal.task.graphql');

export const FollowUpAssessment = ({
  template,
  assessment,
  onClick = () => {},
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.templateTitleHeader}>
      <Icon
        baseClassName="material-icons-outlined"
        className={
          !assessment
            ? styles.templateHeaderIcon
            : assessment?.isDraft
            ? styles.templateHeaderIconInProgress
            : styles.templateHeaderIconDone
        }
        sx={{ fontSize: '1.75rem' }}
      >
        assignment
      </Icon>
      <div className={styles.templateTitleText}>
        <Text
          noMargin
          weight="semiBold"
          onClick={() => !!assessment && onClick()}
          hover={!!assessment}
        >
          {template?.title}
        </Text>
        <Text noMargin size="sm" color="secondary">
          {!assessment
            ? template?.description
            : t(keys.common.CREATED_ON, {
                date: getLocalTime(parseInt(assessment?.dateCreated)).format(
                  'ddd, MMMM Do YYYY HH:mm',
                ),
              })}
        </Text>
      </div>
      {!assessment ? (
        <Button
          align={'right'}
          icon="add"
          value={t(keys.action.START)}
          onClick={onClick}
        />
      ) : !assessment?.isDraft ? (
        <Icon className={styles.check}>check</Icon>
      ) : (
        <Button
          align={'right'}
          size="sm"
          outlined
          value={t(keys.assessments.VIEW)}
          onClick={onClick}
        />
      )}
    </div>
  );
};
export default function ConditionalActionModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { modalState, closeModal, openConfirmationModal } = useModal();
  const [createTask] = useMutation(createTaskMutation);
  const { addInspection } = useInspection();
  const { workspace } = useWorkspace();

  const { show, workspaceId, assessment, online, actions } =
    modalState.conditionalActionModal;

  const { messages, templates, files, popUps } = useMemo(() => {
    const messages = [];
    const templates = [];
    const files = [];
    const popUps = [];
    for (const action of actions) {
      switch (action?.action) {
        case actionsKeys.showMessage:
          messages.push(action);
          break;
        case actionsKeys.popUp:
          popUps.push(action);
          break;
        case actionsKeys.showFile:
          files.push(action);
          break;
        case actionsKeys.followUpTemplate:
          templates.push(action);
          break;
        default:
          break;
      }
    }

    return {
      messages,
      templates,
      files,
      popUps,
    };
  }, [actions]);

  const onHide = () => {
    closeModal({ modalName: modals.conditionalAction });
  };

  const handleTaskCreation = async (selectedTemplateId) => {
    if (online) {
      const tasksToCreate = templates.filter(
        ({ actionable: { id } }) => id !== selectedTemplateId,
      );

      const assessmentToStart = templates.find(
        ({ actionable: { id } }) => id === selectedTemplateId,
      );

      for (const { actionable: template } of tasksToCreate) {
        await createTask({
          variables: {
            title: t(keys.conditionalActions.TASK_TITLE, {
              newTemplate: template?.title,
              oldTemplate: assessment?.template?.title,
            }),
            workspaceId,
            templateId: template?.id,
            assigneeIds: [...assessment?.participants?.map(({ id }) => id)],
            initialInspectionId: assessment?.id,
          },
        });
      }

      if (selectedTemplateId) {
        const { actionable: template } = assessmentToStart;
        await addInspection({
          templateId: template?.id,
          inspectionType: template?.templateType,
          participants: [...assessment?.participants?.map(({ id }) => id)],
          workspaceId,
          privateAssessment: false,
          conditionalMetadata: {
            initialInspectionId: assessment?.id,
            initialTemplateId: assessment?.template?.id,
          },
        }).then(
          ({
            data: {
              addInspection: { id },
            },
          }) =>
            navigate(
              getRoute(
                workspaceId || assessment?.workspace?.id || workspace?.id,
                paths.assessment,
                id,
              ),
            ),
        );
      }
    } else {
      for (const { actionable: template } of templates) {
        await addInspection({
          templateId: template?.id,
          inspectionType: template?.templateType,
          participants: [...assessment?.participants?.map(({ id }) => id)],
          workspaceId,
          privateAssessment: false,
          conditionalMetadata: {
            initialInspectionId: assessment?.id,
            initialTemplateId: assessment?.template?.id,
          },
        });
      }
      navigate(
        getRoute(
          workspaceId || assessment?.workspace?.id || workspace?.id,
          paths.dashboard,
        ),
      );
    }
  };

  return (
    <Modal
      title={t(keys.conditionalActions.MODAL_TITLE)}
      subtitle={t(keys.conditionalActions.MODAL_SUBTITLE)}
      open={show}
      hideSubmit
      submitText={t(keys.action.START)}
      hideCancel
      onClose={() => {
        handleTaskCreation().then(() => onHide());
      }}
    >
      <div className={styles.content}>
        {templates?.length > 0 && (
          <>
            <div>
              <Text noMargin weight="semiBold">
                {t(keys.conditionalActions.FOLLOW_UP_ASSESSMENTS)}
              </Text>
              <Text size="sm" noMargin color="secondary">
                {t(keys.conditionalActions.FOLLOW_UP_ASSESSMENTS_SUBTEXT)}
              </Text>
            </div>
            <div className={styles.list}>
              {templates.map(({ actionable }, i) => (
                <div key={`template-action-${i}`}>
                  <FollowUpAssessment
                    template={actionable}
                    onClick={() =>
                      handleTaskCreation(actionable?.id).then(() => onHide())
                    }
                  />
                </div>
              ))}
            </div>
          </>
        )}
        {files?.length > 0 && (
          <>
            <div>
              <Text noMargin weight="semiBold">
                {t(keys.conditionalActions.FILES)}
              </Text>
              <Text noMargin size="sm" color="secondary">
                {t(keys.conditionalActions.FILES_SUBTEXT)}
              </Text>
            </div>
            <div className={styles.list}>
              {files.map(({ actionable }, i) => (
                <div key={`file-action-${i}`}>
                  <SimpleFileCard
                    file={actionable}
                    extension={actionable?.extension}
                    fileName={actionable?.name}
                  />
                </div>
              ))}
            </div>
          </>
        )}
        {messages?.length > 0 && (
          <>
            <div>
              <Text noMargin weight="semiBold">
                {t(keys.conditionalActions.MESSAGES)}
              </Text>
              <Text noMargin color="secondary" size="sm">
                {t(keys.conditionalActions.MESSAGE_SUBTEXT)}
              </Text>
            </div>
            {messages.map(({ additionalData: { message } }, i) => (
              <div key={`message-action-${i}`}>
                <Text noMargin weight="semiBold">
                  {message}
                </Text>
              </div>
            ))}
            {popUps?.length > 0 &&
              popUps.map(({ additionalData: { title, message } }, i) => (
                <div key={`popup-action-${i}`}>
                  <Text
                    hover
                    noMargin
                    weight="semiBold"
                    color="accentPrimary"
                    onClick={() =>
                      openConfirmationModal({
                        title,
                        description: message,
                        hideSubmit: true,
                        variant: 'warning',
                        cancelText: t(keys.action.CANCEL),
                      })
                    }
                  >
                    {title}
                  </Text>
                </div>
              ))}
          </>
        )}
      </div>
    </Modal>
  );
}
